import { isArray } from '@ember/array';
import { get } from '@ember/object';
import { isNone } from '@ember/utils';
import { typeOf } from '@ember/utils';
import TemplateModel from 'cio/models/template';
import { Platform } from 'cio/utils/push/platforms';
import { isEmpty } from '@ember/utils';
import { Editor } from 'cio/services/editor';

const EMPTY_TITLE = '(No subject)';

export const DEFAULT_CUSTOM_PAYLOAD_BODY_JSON = '{ "android": "{}", "ios": "{}" }';

export type Payload = {
  ios?: Record<string, unknown>;
  android?: Record<string, unknown>;
};

type ApsType = {
  alert: {
    body: string;
    title: string;
  };
  'mutable-content': number;
  sound: string;
};

type IosMessageType = {
  apns?: {
    payload?: {
      CIO?: {
        push?: {
          image: string;
          link: string;
        };
      };
      aps: ApsType;
      test?: string;
    };
  };
};

type IosPayloadType = {
  message?: IosMessageType;
  aps?: ApsType;
};

// APNs payloads:
// https://developer.apple.com/library/archive/documentation/NetworkingInternet/Conceptual/RemoteNotificationsPG/CreatingtheNotificationPayload.html

// FCM payloads:
// https://firebase.google.com/docs/reference/fcm/rest/v1/projects.messages

export const PAYLOAD_PATHS = {
  title: {
    ios: ['message.notification.title', 'message.apns.payload.aps.alert.title', 'aps.alert.title'],
    android: [
      'message.android.notification.title',
      'message.notification.title',
      'message.android.data.title',
      'message.data.title',
    ],
  },

  body: {
    ios: [
      'message.notification.body',
      'message.apns.payload.aps.alert.body',
      'message.apns.payload.aps.alert',
      'aps.alert.body',
      'aps.alert',
    ],
    android: [
      'message.android.notification.body',
      'message.notification.body',
      'message.android.data.body',
      'message.data.body',
    ],
  },

  image: {
    ios: (payload: Payload['ios']) => {
      let aps = getAps(payload);
      let mutableContent = aps?.['mutable-content'];
      if (isNone(mutableContent)) {
        return null;
      }

      return ['image-url', 'image_url', 'imageUrl', 'CIO.push.image', 'message.apns.payload.CIO.push.image'];
    },

    android: [
      'message.android.data.image',
      'message.data.image',
      'message.android.data.image_url',
      'message.data.image_url',
    ],
  },
  link: {
    ios: (payload: Payload['ios']) => {
      let aps = getAps(payload);
      let mutableContent = aps?.['mutable-content'];
      if (isNone(mutableContent)) {
        return null;
      }

      return ['CIO.push.link', 'message.apns.payload.CIO.push.link'];
    },

    android: ['message.android.data.link', 'message.data.link'],
  },
};

export const getPayloadValue = (
  payload: Record<string, unknown>,
  platform: Platform,
  key: keyof typeof PAYLOAD_PATHS,
) => {
  if (typeof payload === 'string') {
    return '';
  }

  let path: string | string[] | ((payload: Record<string, any>) => string[]) | undefined | null = get(
    PAYLOAD_PATHS,
    `${key}.${platform}` as any,
  );

  if (typeOf(path) === 'function') {
    path = (path as (payload: Record<string, any>) => string[])(payload);
  }

  if (isNone(path)) {
    return '';
  }

  if (isArray(path)) {
    for (let i = 0; i < path.length; i++) {
      let value = get(payload, path[i]);
      if (typeof value === 'string') {
        return value;
      }
    }

    return '';
  }

  let value = get(payload, path as unknown as string);
  if (typeof value === 'string') {
    return value;
  }

  return '';
};

export const findFirstNonEmptyTitle = (jsonData = '') => {
  // Use regex to determine the order of iOS and Android. Returns an array in
  // order of appearance.
  let deviceOrder = jsonData.match(/(ios|android)/g) as Platform[] | null;
  let body: Payload;

  if (deviceOrder == null || deviceOrder.length === 0) {
    return EMPTY_TITLE;
  }

  try {
    body = JSON.parse(jsonData);

    // backend returns ios and android content as a string (eg: { "android": "{ \"message\": ... }", "ios": "{ \"aps\": ... }"})
    body.android = parseIfStillString(body.android);
    body.ios = parseIfStillString(body.ios);
  } catch (e) {
    return EMPTY_TITLE;
  }

  let title = getPayloadValue(body[deviceOrder[0]] ?? {}, deviceOrder[0] as Platform, 'title');

  // First device doesn't have a title, try the second if it exists
  if (!title && deviceOrder.length === 2) {
    title = getPayloadValue(body[deviceOrder[1]] ?? {}, deviceOrder[1] as Platform, 'title');
  }

  return title || EMPTY_TITLE;
};

function getAps(payload: IosPayloadType | undefined) {
  return payload?.message?.apns?.payload?.aps ?? payload?.aps;
}

function parseIfStillString(payload: any) {
  if (!isNone(payload) && typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {}
  }
  return payload;
}

export const isTemplateTypePush = (template: TemplateModel): boolean => {
  return template.templateType === 'push';
};

export const isCustomPush = (template: TemplateModel): boolean => {
  return isTemplateTypePush(template) && template.editor === Editor.JSON;
};

export const isCustomPushPayloadEmpty = (template: TemplateModel): boolean => {
  let { hasContent } = template;
  if (isCustomPush(template)) {
    // hasContent is set to true when the custom payload is {}. resetting it to false so that it
    // becomes true (and stays true) when either android or ios has content
    hasContent = false;
    try {
      let json = JSON.parse(template.bodyJson);

      if (!isEmpty(json.android)) {
        let android = JSON.parse(json.android);
        hasContent ||= Object.keys(android).length > 0;
      }

      if (!isEmpty(json.ios)) {
        let ios = JSON.parse(json.ios);
        hasContent ||= Object.keys(ios).length > 0;
      }
    } catch {
      // likely err-ed on JSON parse, i.e. content is present
      hasContent = true;
    }
  } else {
    return false;
  }

  return !hasContent;
};
